import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Video, FileText, Mic, Download, Sparkles, LogIn } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cc23a1c1-e998-45d0-adb8-2e9c1c544201/artifacts/g7bc3tyb_image.png";

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FlowSnap" className="h-16" />
          </div>
          <div className="flex items-center gap-3">
            <Button 
              data-testid="login-header-btn"
              onClick={() => navigate('/login')}
              className="bg-primary text-white hover:bg-blue-700"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Text Content */}
          <motion.div 
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">AI-Powered Documentation</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Turn Videos into
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Beautiful Guides</span>
            </h1>
            
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Upload a video and let AI transform it into professional step-by-step documentation. 
              Add voice-overs, edit content, and export to PDF or Word in minutes.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                data-testid="login-hero-btn"
                onClick={() => navigate('/login')}
                size="lg"
                className="bg-primary text-white hover:bg-blue-700 shadow-sm"
              >
                <LogIn className="mr-2 w-4 h-4" /> Login to Get Started
              </Button>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg transform rotate-3"></div>
              <img 
                src="https://images.unsplash.com/photo-1763897913733-0d228fce7b5b" 
                alt="Documentation platform" 
                className="relative rounded-lg shadow-xl w-full"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Create Perfect Guides
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Powerful features designed for product teams, technical writers, and support professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Video,
                title: 'Video Analysis',
                description: 'AI automatically extracts key frames and generates step-by-step instructions',
                color: 'bg-blue-50 text-blue-600'
              },
              {
                icon: FileText,
                title: 'Rich Editor',
                description: 'Edit content, add screenshots, and customize your documentation',
                color: 'bg-indigo-50 text-indigo-600'
              },
              {
                icon: Mic,
                title: 'AI Voice-over',
                description: 'Generate natural-sounding voice narrations with multiple voice options',
                color: 'bg-purple-50 text-purple-600'
              },
              {
                icon: Download,
                title: 'Export Anywhere',
                description: 'Download as PDF or Word documents ready to share with your team',
                color: 'bg-pink-50 text-pink-600'
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg border border-slate-200 p-8 card-hover"
              >
                <div className={`w-12 h-12 rounded-md ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Ready to Transform Your Videos?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Start creating professional documentation in minutes. No credit card required.
            </p>
            <Button 
              data-testid="login-cta-btn"
              onClick={() => navigate('/login')}
              size="lg"
              className="bg-primary text-white hover:bg-blue-700 shadow-sm"
            >
              <LogIn className="mr-2 w-4 h-4" /> Login to Start Creating
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-600">
          <p>&copy; 2024 FlowSnap. Built with AI to make documentation effortless.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;